import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { formatUsuarioLabel } from '@leadsphere/types';

import { Button } from '../../components/ui/Button.js';
import { CheckboxDropdown } from '../../components/ui/CheckboxDropdown.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useClientesLookup } from '../../hooks/useClientesLookup.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError, uploadArchivo } from '../../lib/api.js';
import { DropzoneArchivo, fmtTamano, iconoPorMime, validarArchivoLocal } from './AdjuntosUploader.js';
import type { ClienteListItem } from '../clientes/types.js';
import type { TicketTipoCatalogo, UsuarioAsignable } from './types.js';

interface ArchivoLocal {
  file: File;
  advertencia: string | null;
}

interface SubtareaForm {
  tipo_solicitud_id: string;
  detalle: string;
}

// SRS-02 §8.3 — preview inmediato en el form; el backend vuelve a resolver
// el SLA real desde ticket_sla_config al crear.
const SLA_PREVIEW: Record<string, { resp: number; reso: number }> = {
  P1: { resp: 4, reso: 24 },
  P2: { resp: 8, reso: 72 },
  P3: { resp: 24, reso: 120 },
};

function fmtFechaHora(d: Date): string {
  return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Prompt AL — reemplaza a NuevoTicketModal.tsx: página completa (mismo
// patrón que /clientes/nuevo), ya no un Modal.
export function NuevoTicket() {
  const { get, post } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const esRoot = user?.rol === 'root';
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [solicitanteId, setSolicitanteId] = useState('');
  const [titulo, setTitulo] = useState('');
  // Orden de selección = orden de llegada al backend — determina cuál área
  // se usa como tickets.area_responsable (la del primer responsable elegido).
  const [responsableIds, setResponsableIds] = useState<string[]>([]);
  const [subtareasForm, setSubtareasForm] = useState<Record<string, SubtareaForm>>({});
  const [prioridad, setPrioridad] = useState<'P1' | 'P2' | 'P3' | ''>('');
  const [descripcion, setDescripcion] = useState('');
  const [esExterno, setEsExterno] = useState(false);
  const [clienteIds, setClienteIds] = useState<string[]>([]);
  const [validationError, setValidationError] = useState('');
  const [fechaPreview] = useState<Date>(() => new Date());
  const [archivosLocales, setArchivosLocales] = useState<ArchivoLocal[]>([]);
  const [estadoEnvio, setEstadoEnvio] = useState<'idle' | 'creando' | 'subiendo'>('idle');
  const [subidaActual, setSubidaActual] = useState({ hecho: 0, total: 0 });

  // Prompt BJ-11 — /usuarios/asignables en vez de /usuarios: este dropdown es
  // para CUALQUIER rol autenticado (crear/asignar ticket), GET /usuarios está
  // restringido a root/head/analista/kam/CEO (gestión de usuarios) y causaba
  // 403 a digital/comercial/marketing.
  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios-asignables'],
    queryFn: () => get<UsuarioAsignable[]>('/usuarios/asignables'),
  });
  const usuariosActivos = useMemo(() => (usuariosData ?? []).filter(u => u.activo), [usuariosData]);

  const { data: clientesData } = useClientesLookup({ enabled: esExterno });
  const clientesOpts: ClienteListItem[] = clientesData?.data ?? [];
  const usuarioSesion = useMemo(() => usuariosActivos.find(u => u.correo === user?.correo), [usuariosActivos, user?.correo]);

  useEffect(() => {
    if (!esRoot && usuarioSesion) setSolicitanteId(usuarioSesion.id);
  }, [esRoot, usuarioSesion]);

  const solicitante = useMemo(() => usuariosActivos.find(u => u.id === solicitanteId), [usuariosActivos, solicitanteId]);
  const responsablesSeleccionados = useMemo(
    () => responsableIds.map(id => usuariosActivos.find(u => u.id === id)).filter((u): u is UsuarioAsignable => Boolean(u)),
    [usuariosActivos, responsableIds],
  );
  const areasResponsables = useMemo(
    () => [...new Set(responsablesSeleccionados.map(u => u.area).filter((a): a is string => Boolean(a)))],
    [responsablesSeleccionados],
  );

  // Un catálogo por área única de los responsables elegidos — cada fila de
  // la tabla filtra SOLO por el área de esa persona (no la unión de todas,
  // a diferencia del cascade viejo de NuevoTicketModal).
  const catalogosPorArea = useQueries({
    queries: areasResponsables.map(area => ({
      queryKey: ['tickets-catalogo', area],
      queryFn: () => get<TicketTipoCatalogo[]>(`/tickets/catalogo/${encodeURIComponent(area)}`),
    })),
  });
  const tiposPorArea = useMemo(() => {
    const mapa = new Map<string, TicketTipoCatalogo[]>();
    areasResponsables.forEach((area, i) => mapa.set(area, catalogosPorArea[i]?.data ?? []));
    return mapa;
  }, [areasResponsables, catalogosPorArea]);
  const cargandoTipos = catalogosPorArea.some(q => q.isFetching);

  function handleResponsablesChange(ids: string[]) {
    setResponsableIds(ids);
    setSubtareasForm(prev => {
      const next: Record<string, SubtareaForm> = {};
      for (const id of ids) next[id] = prev[id] ?? { tipo_solicitud_id: '', detalle: '' };
      return next;
    });
  }

  function updateSubtarea(responsableId: string, changes: Partial<SubtareaForm>) {
    setSubtareasForm(prev => ({ ...prev, [responsableId]: { ...prev[responsableId]!, ...changes } }));
  }

  function handleNivelServicioChange(externo: boolean) {
    setEsExterno(externo);
    if (!externo) setClienteIds([]);
  }

  function handleArchivosSeleccionados(files: File[]) {
    setArchivosLocales(prev => [...prev, ...files.map(file => ({ file, advertencia: validarArchivoLocal(file) }))]);
  }

  function quitarArchivoLocal(index: number) {
    setArchivosLocales(prev => prev.filter((_, i) => i !== index));
  }

  const enviando = estadoEnvio !== 'idle';

  async function handleGuardar() {
    if (!solicitanteId || !titulo.trim() || !responsableIds.length || !prioridad || descripcion.trim().length < 10) {
      setValidationError('Completa Solicitante, Título, al menos un Responsable, Prioridad, y una Descripción de al menos 10 caracteres.');
      return;
    }
    const filaIncompleta = responsableIds.find(id => {
      const fila = subtareasForm[id];
      return !fila?.tipo_solicitud_id || fila.detalle.trim().length < 10;
    });
    if (filaIncompleta) {
      setValidationError('Cada responsable necesita Tipo de solicitud y Detalle (mínimo 10 caracteres) en la tabla de sub-tareas.');
      return;
    }
    // Espejo de la validación del backend (superRefine en crearTicketSchema) — solo UX.
    if (esExterno && !clienteIds.length) {
      setValidationError('Selecciona al menos un cliente cuando el nivel de servicio es Externo.');
      return;
    }
    setValidationError('');
    setEstadoEnvio('creando');

    try {
      const ticket = await post<{ id: string; folio: string }>('/tickets', {
        solicitante_id: solicitanteId,
        titulo: titulo.trim(),
        subtareas: responsableIds.map(id => ({
          responsable_id: id,
          tipo_solicitud_id: subtareasForm[id]!.tipo_solicitud_id,
          detalle: subtareasForm[id]!.detalle.trim(),
        })),
        prioridad,
        descripcion: descripcion.trim(),
        nivel_servicio_es_externo: esExterno,
        cliente_ids: esExterno ? clienteIds : [],
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });

      if (archivosLocales.length) {
        setEstadoEnvio('subiendo');
        const fallidos: string[] = [];
        for (let i = 0; i < archivosLocales.length; i++) {
          setSubidaActual({ hecho: i, total: archivosLocales.length });
          try {
            await uploadArchivo(`/tickets/${ticket.id}/adjuntos/archivo`, archivosLocales[i]!.file);
          } catch (err) {
            fallidos.push(`${archivosLocales[i]!.file.name}${err instanceof ApiError ? ` (${err.message})` : ''}`);
          }
        }
        if (fallidos.length) {
          showToast('error', `Ticket ${ticket.folio} creado, pero no se pudo adjuntar: ${fallidos.join(', ')}`);
        } else {
          showToast('success', `Ticket ${ticket.folio} creado con ${archivosLocales.length} adjunto${archivosLocales.length !== 1 ? 's' : ''}.`);
        }
      } else {
        showToast('success', `Ticket ${ticket.folio} creado correctamente.`);
      }

      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Ocurrió un error al crear el ticket.');
      setEstadoEnvio('idle');
    }
  }

  const slaPreview = prioridad ? SLA_PREVIEW[prioridad] : null;

  return (
    <div className="nc-wrap">
      <Link to="/tickets" className="back-btn">
        ← Volver a tickets
      </Link>

      <div style={{ marginBottom: 22 }}>
        <div className="nc-title">Nuevo Ticket</div>
        <div className="nc-sub">Completa la información para levantar una nueva solicitud</div>
      </div>

      {validationError && <div className="form-error-banner">{validationError}</div>}

      <div className="fg">
        <div className="fs-ttl">Datos automáticos</div>
        <div className="ff">
          <label>ID / Folio</label>
          <div className="ff-calculated">Se asigna al guardar</div>
        </div>
        <div className="ff">
          <label>Fecha de creación</label>
          <div className="ff-calculated">{fmtFechaHora(fechaPreview)}</div>
        </div>

        <div className="fs-ttl">Solicitante</div>
        <div className="ff">
          <label>Solicitante *</label>
          {esRoot ? (
            <select value={solicitanteId} onChange={e => setSolicitanteId(e.target.value)}>
              <option value="">Seleccionar usuario...</option>
              {usuariosActivos.map(u => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          ) : (
            <div className="ff-calculated">{usuarioSesion?.nombre ?? '—'}</div>
          )}
        </div>
        <div className="ff">
          <label>Área solicitante</label>
          <div className="ff-calculated">{solicitanteId ? (solicitante?.area ?? 'Sin área asignada') : 'Se inyecta automáticamente'}</div>
        </div>

        <div className="ff full">
          <label>Título *</label>
          <input type="text" placeholder="Ej: Falta de acceso a reporte de ventas" value={titulo} onChange={e => setTitulo(e.target.value)} />
        </div>

        <div className="fs-ttl">Solicitud</div>
        <div className="ff">
          <label>Prioridad *</label>
          <select value={prioridad} onChange={e => setPrioridad(e.target.value as 'P1' | 'P2' | 'P3')}>
            <option value="">Seleccionar...</option>
            <option value="P1">P1 — Urgente</option>
            <option value="P2">P2 — Normal</option>
            <option value="P3">P3 — Programable</option>
          </select>
        </div>

        {slaPreview && (
          <div className="ff full">
            <div
              style={{
                background: 'var(--card-hl)',
                border: '1px solid #b3d7f0',
                borderRadius: 9,
                padding: '10px 16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 10,
                fontSize: 12,
              }}
            >
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 3 }}>Resp. SLA</div>
                <div style={{ fontWeight: 700, color: 'var(--heading)', fontSize: 15 }}>{slaPreview.resp}h</div>
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 3 }}>Reso. SLA</div>
                <div style={{ fontWeight: 700, color: 'var(--heading)', fontSize: 15 }}>{slaPreview.reso}h</div>
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 3 }}>TPR (hrs)</div>
                <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>Auto al tomar</div>
              </div>
              <div>
                <div style={{ color: 'var(--muted)', marginBottom: 3 }}>TTR (hrs)</div>
                <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>Auto al cerrar</div>
              </div>
            </div>
          </div>
        )}

        <div className="fs-ttl">Nivel de Servicio</div>
        <div className="ff full">
          <label>Interno / Externo *</label>
          <div className="check-row">
            <input type="checkbox" id="tk-externo" checked={esExterno} onChange={e => handleNivelServicioChange(e.target.checked)} />
            <label htmlFor="tk-externo">Es un ticket Externo (asociado a uno o más clientes)</label>
          </div>
        </div>
        {esExterno && (
          <div className="ff full">
            <label>Cliente(s) *</label>
            <CheckboxDropdown
              options={clientesOpts.map(c => ({ value: c.id, label: c.nombre_comercial }))}
              selected={clienteIds}
              onChange={setClienteIds}
              placeholder="Seleccionar cliente(s)..."
            />
          </div>
        )}

        <div className="ff full">
          <label>Descripción *</label>
          <textarea
            rows={4}
            placeholder="Describe a grandes rasgos la solicitud…"
            value={descripcion}
            onChange={e => setDescripcion(e.target.value)}
          />
        </div>

        <div className="fs-ttl">Responsables y Sub-tareas</div>
        <div className="ff full">
          <label>Responsable(s) *</label>
          <CheckboxDropdown
            // Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno.
            options={usuariosActivos.map(u => ({ value: u.id, label: u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre }))}
            selected={responsableIds}
            onChange={handleResponsablesChange}
            placeholder="Seleccionar responsables..."
          />
          {responsablesSeleccionados.some(u => !u.area) && (
            <span className="ff-hint">Uno o más responsables seleccionados no tienen área asignada — no pueden ser responsables de un ticket.</span>
          )}
        </div>

        {responsableIds.length > 0 && (
          <div className="ff full">
            <table className="ctbl">
              <thead>
                <tr>
                  <th>Responsable</th>
                  <th>Área</th>
                  <th>Tipo de solicitud</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {responsableIds.map(id => {
                  const u = usuariosActivos.find(x => x.id === id);
                  const tipos = u?.area ? (tiposPorArea.get(u.area) ?? []) : [];
                  const fila = subtareasForm[id] ?? { tipo_solicitud_id: '', detalle: '' };
                  return (
                    <tr key={id}>
                      <td>{u?.nombre ?? '—'}</td>
                      <td style={{ color: 'var(--muted)' }}>{u?.area ?? 'Sin área'}</td>
                      <td>
                        <select
                          value={fila.tipo_solicitud_id}
                          disabled={!u?.area}
                          onChange={e => updateSubtarea(id, { tipo_solicitud_id: e.target.value })}
                        >
                          <option value="">{cargandoTipos ? 'Cargando…' : 'Seleccionar...'}</option>
                          {tipos.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.tipo}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <textarea
                          rows={2}
                          placeholder="Detalle de la sub-tarea…"
                          value={fila.detalle}
                          onChange={e => updateSubtarea(id, { detalle: e.target.value })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="ff full">
          <label>Área responsable</label>
          <div className="ff-calculated">{areasResponsables.length ? `${areasResponsables[0]} (del primer responsable elegido)` : 'Se inyecta automáticamente'}</div>
        </div>

        <div className="ff full">
          <label>Adjuntos (opcional)</label>
          {archivosLocales.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {archivosLocales.map((a, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 7 }}
                >
                  <span style={{ fontSize: 14 }}>{iconoPorMime(a.file.type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.file.name}
                    </div>
                    <div style={{ fontSize: 11, color: a.advertencia ? 'var(--danger)' : 'var(--muted)' }}>
                      {fmtTamano(a.file.size)}
                      {a.advertencia ? ` — ${a.advertencia}` : ''}
                    </div>
                  </div>
                  <button type="button" className="trash-btn" aria-label="Quitar adjunto" disabled={enviando} onClick={() => quitarArchivoLocal(i)}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <DropzoneArchivo onFilesSelected={handleArchivosSeleccionados} disabled={enviando} />
          <span className="ff-hint">Máx. 10MB por archivo. También puedes adjuntar links directamente en el ticket ya creado.</span>
        </div>
      </div>

      <div className="nc-actions">
        <Button variant="secondary" onClick={() => navigate('/tickets')} disabled={enviando}>
          Cancelar
        </Button>
        <Button onClick={() => void handleGuardar()} disabled={enviando}>
          {estadoEnvio === 'creando' && 'Creando…'}
          {estadoEnvio === 'subiendo' && `Subiendo adjuntos ${subidaActual.hecho + 1} de ${subidaActual.total}…`}
          {estadoEnvio === 'idle' && '+ Crear Ticket'}
        </Button>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
